// apps/intranet/src/auth/useEffectivePermissions.ts
// ──────────────────────────────────────────────────────────────────────────────
// Frontend hook for runtime authorisation. Reads /api/me/permissions, which
// resolves the current user's Entra role → ops.RoleModuleAccess matrix.
//
// Source of truth:
//   • Entra ID  → WHO has WHAT role
//   • ops.RoleModuleAccess → WHAT each role can do
//
// Replaces the registry.ts hardcoded `roles: [...]` arrays. Sidebar
// visibility, RoleGuard route gating, and conditional UI elements all
// resolve through this single hook.
//
// Behaviour:
//   • IT.Admin sees everything (modules === '*' sentinel from the API).
//   • Cache: 60 s staleTime in TanStack Query.
//   • Loading state: callers should render their own placeholder; the
//     `isLoading` flag is exposed alongside the helpers.
// ──────────────────────────────────────────────────────────────────────────────

import { useQuery } from '@tanstack/react-query'

export interface EffectivePermissionsResponse {
  role: string | null
  user: string | null
  isAdmin: boolean
  /** '*' = universal access (IT.Admin); object map otherwise. */
  modules: '*' | Record<string, { read: boolean; write: boolean }>
}

async function fetchEffectivePermissions(): Promise<EffectivePermissionsResponse> {
  const res = await fetch('/api/me/permissions', { credentials: 'include' })
  if (!res.ok) throw new Error(`/api/me/permissions ${res.status}`)
  return res.json()
}

export function useEffectivePermissions() {
  const q = useQuery({
    queryKey: ['me-permissions'],
    queryFn: fetchEffectivePermissions,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    retry: 1,
  })

  const data = q.data
  const isAdmin = data?.isAdmin === true || data?.modules === '*'

  /** Can the current user READ this module? Defensive against malformed payload. */
  const canRead = (moduleId: string): boolean => {
    if (!data) return false
    if (data.modules === '*') return true
    if (!data.modules || typeof data.modules !== 'object') return false
    const m = (data.modules as Record<string, { read?: boolean; write?: boolean } | undefined>)[moduleId]
    return !!m?.read
  }

  /** Can the current user WRITE to this module? Defensive against malformed payload. */
  const canWrite = (moduleId: string): boolean => {
    if (!data) return false
    if (data.modules === '*') return true
    if (!data.modules || typeof data.modules !== 'object') return false
    const m = (data.modules as Record<string, { read?: boolean; write?: boolean } | undefined>)[moduleId]
    return !!m?.write
  }

  return {
    isLoading: q.isLoading,
    isError: q.isError,
    error: q.error,
    refetch: q.refetch,
    role: data?.role ?? null,
    user: data?.user ?? null,
    isAdmin,
    canRead,
    canWrite,
    raw: data,
  }
}
